import { useCallback, useSyncExternalStore } from 'react';

type MediaStore = {
  query: MediaQueryList;
  listeners: Set<() => void>;
  notify: () => void;
};
const stores = new Map<string, MediaStore>();
function storeFor(query: string): MediaStore {
  const existing = stores.get(query);
  if (existing) return existing;
  const listeners = new Set<() => void>();
  const store = {
    query: window.matchMedia(query),
    listeners,
    notify: () => {
      for (const listener of listeners) listener();
    },
  };
  stores.set(query, store);
  return store;
}
const getServerSnapshot = () => false;

/** SSR uses false; mounted consumers share one browser listener per query. */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (
        typeof window === 'undefined' ||
        typeof window.matchMedia !== 'function'
      )
        return () => {};
      const store = storeFor(query);
      if (store.listeners.size === 0)
        store.query.addEventListener('change', store.notify);
      store.listeners.add(onStoreChange);
      return () => {
        store.listeners.delete(onStoreChange);
        if (store.listeners.size === 0) {
          store.query.removeEventListener('change', store.notify);
          stores.delete(query);
        }
      };
    },
    [query]
  );
  const getSnapshot = useCallback(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      storeFor(query).query.matches,
    [query]
  );
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
